import { useEffect, useState } from 'react';

/**
 * Carga diferida: no pedirle nada al backend hasta que haga falta.
 *
 * El backend vive en el plan gratuito de Render, que apaga el servicio cuando no
 * hay trafico. La primera consulta despierta el proceso y puede tardar bastante.
 * La portada pedia varias cosas apenas se montaba —eventos publicos, ajustes de la
 * app, el banner del sponsor— asi que quien llegaba por primera vez esperaba ese
 * arranque antes de ver nada util, aunque el contenido de la portada es texto e
 * imagenes que ya vienen en el bundle.
 *
 * Con esto la portada pinta de inmediato y cada seccion pide sus datos recien
 * cuando el visitante se acerca. Si nunca baja, nunca se despierta el backend.
 */

/**
 * Se enciende una sola vez, cuando el elemento se acerca a la pantalla.
 *
 * Mide con getBoundingClientRect sobre los eventos de scroll y resize, en vez de
 * usar IntersectionObserver. El observador es la herramienta canonica para esto,
 * pero depende del compositor: en un contexto que no esta pintando cuadros no
 * entrega NUNCA, ni siquiera la primera notificacion que promete la especificacion
 * —comprobado en este proyecto—, y ahi la seccion se queda en su esqueleto para
 * siempre. Medir a mano es un poco mas tosco pero no puede quedarse callado, y el
 * costo es un listener pasivo que se suelta apenas dispara.
 */
export function useCercaDeLaPantalla(ref, margen = 300) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;                 // una vez encendido no se vuelve a mirar
    const el = ref.current;
    if (!el) return;

    let soltar = () => {};

    const revisar = () => {
      const r = el.getBoundingClientRect();
      // "Le falta menos que el margen para asomar por abajo" cubre tambien el caso
      // de que ya haya pasado de largo (top negativo).
      if (r.top - window.innerHeight <= margen) {
        setVisible(true);
        soltar();
      }
    };

    window.addEventListener('scroll', revisar, { passive: true });
    window.addEventListener('resize', revisar);

    // Red de seguridad: hay contextos donde el evento de scroll simplemente no se
    // entrega —lo mismo que le pasa a IntersectionObserver— y ahi la seccion se
    // quedaria en su esqueleto para siempre. Un vistazo cada cuarto de segundo
    // cuesta practicamente nada y se apaga apenas dispara.
    const reloj = setInterval(revisar, 250);

    soltar = () => {
      clearInterval(reloj);
      window.removeEventListener('scroll', revisar);
      window.removeEventListener('resize', revisar);
    };

    // La primera comprobacion va en el siguiente turno, no ahora: recien montado el
    // navegador todavia no termino de maquetar y todo parece estar pegado al tope,
    // asi que el pie de pagina —que arranca pasados los 3000px— se daba por visible
    // al instante y el diferido no servia de nada.
    const primera = setTimeout(revisar, 0);

    return () => { clearTimeout(primera); soltar(); };
  }, [ref, margen, visible]);

  return visible;
}

/**
 * Se enciende cuando el navegador queda desocupado.
 *
 * Para lo que esta arriba del todo y por lo tanto siempre a la vista: medir la
 * posicion no sirve de nada ahi, pero igual conviene que no compita con la primera
 * pintura.
 */
export function useNavegadorDesocupado(esperaMaxima = 2000) {
  const [listo, setListo] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setListo(true), { timeout: esperaMaxima });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setListo(true), 400);   // Safari no tiene requestIdleCallback
    return () => clearTimeout(t);
  }, [esperaMaxima]);

  return listo;
}
